import {
  TaskTodayCompleteOptions,
  useTaskTodayControls as _useTaskTodayControls,
} from '@griply/hooks';
import {
  ActionEvents,
  ID,
  ScheduleEntryState,
  Task,
  TaskType,
  UpdateTask,
} from '@griply/types';
import {
  TaskCompletionAction,
  TaskCompletionActionKind,
  taskTypeToTrackingTaskType,
} from '@griply/utils';
import { useCallback, useMemo } from 'react';
import { useOpenManualEntryLogging } from 'shared/contexts/habit-manual-entry-logging';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useUpdateGoalTaskCountMutation } from 'shared/hooks/queries/use-update-goal-task-count-mutation';
import { useUpdateTasksMutation } from 'shared/hooks/queries/use-update-tasks-mutation';
import { useRecordPmfActivity } from 'shared/hooks/use-record-pmf-activity';
import { useTrackEvents } from 'shared/hooks/use-track-events';

import { useUpdateTask } from './use-update-task';
import { useUpdateTaskLinks } from './use-update-task-links';

type CompleteOptions = TaskTodayCompleteOptions & {
  keepDetailOpen?: boolean;
};

export const useTaskTodayControls = () => {
  const today = useToday();
  const { submit: updateTask } = useUpdateTask();
  const { mutate: updateTasks } = useUpdateTasksMutation();
  const { mutate: updateGoalTaskCount } = useUpdateGoalTaskCountMutation();
  const updateTaskLinks = useUpdateTaskLinks();
  const openTaskDetail = useOpenTaskDetail();
  const openManualEntryLogging = useOpenManualEntryLogging();
  const track = useTrackEvents();
  const { recordCompletion } = useRecordPmfActivity();

  const handlers = useMemo(
    () => ({
      updateTask: (update: UpdateTask) => {
        updateTask(update);
      },
      updateHabitSchedules: (updates: UpdateTask[]) => {
        updateTasks(updates);
      },
      updateGoalTaskCount: ({
        goalId,
        delta,
      }: {
        goalId: ID;
        delta: 1 | -1;
      }) => {
        updateGoalTaskCount({ goalId, completedTaskCount: delta });
      },
      openManualEntry: (habitId: ID, date: Date) => {
        openManualEntryLogging(habitId, date);
      },
      trackTaskComplete: (taskType: TaskType) => {
        track(ActionEvents.TaskComplete, {
          type: taskTypeToTrackingTaskType[taskType],
        });
        recordCompletion();
      },
    }),
    [
      updateTask,
      updateTasks,
      updateGoalTaskCount,
      openManualEntryLogging,
      track,
      recordCompletion,
    ],
  );

  const { onComplete: sharedOnComplete } = _useTaskTodayControls({
    today,
    handlers,
  });

  const onComplete = useCallback(
    (
      task: Task,
      type: ScheduleEntryState = ScheduleEntryState.Complete,
      options: CompleteOptions = {},
    ): TaskCompletionAction[] => {
      const actions = sharedOnComplete(task, type, options);

      const hasOneOffOrManual = actions.some(
        (action) =>
          action.kind === TaskCompletionActionKind.OpenManualEntry ||
          action.kind === TaskCompletionActionKind.UpdateTask,
      );
      const hasHabitFullCompletion =
        task.type !== TaskType.Task &&
        actions.some(
          (action) =>
            action.kind === TaskCompletionActionKind.TrackTaskComplete,
        );

      const shouldCloseDetail =
        (hasOneOffOrManual &&
          (!options.keepDetailOpen || !task.parentIds?.length)) ||
        (hasHabitFullCompletion && !options.keepDetailOpen);
      if (shouldCloseDetail) {
        openTaskDetail(undefined);
      }

      return actions;
    },
    [sharedOnComplete, openTaskDetail],
  );

  const onUpdate = useCallback(
    (initialTask: Task, updatedTask: UpdateTask) => {
      updateTaskLinks([{ task: initialTask, update: updatedTask }]);
    },
    [updateTaskLinks],
  );

  return {
    onUpdate,
    onComplete,
    openManualEntryLogging,
  };
};
